import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Bot,
  Eye,
  Loader2,
  Lock,
  LogOut,
  Mail,
  MessageSquare,
  MessagesSquare,
  ShieldCheck,
  User,
} from "lucide-react";
import { AdminQuotes } from "@/components/portfolio/AdminQuotes";
import { AdminProjects } from "@/components/portfolio/AdminProjects";
import { AdminPayments } from "@/components/portfolio/AdminPayments";
import { PageLoader } from "@/components/portfolio/PageLoader";
import { API, formatApiErrorDetail } from "@/lib/api";

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return "";
  }
};

const GoogleButton = () => (
  <button
    data-testid="admin-google-btn"
    onClick={() => {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + "/admin";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }}
    className="flex w-full items-center justify-center gap-3 rounded-full border border-[#2A2A33] bg-[#101014] px-6 py-3.5 text-sm font-semibold text-zinc-200 transition-all duration-300 hover:border-[#E50914]/60 hover:text-white"
  >
    <svg className="h-5 w-5" viewBox="0 0 24 24">
      <path fill="#EA4335" d="M12 5.04c1.62 0 3.06.56 4.2 1.64l3.12-3.12C17.46 1.8 14.96.75 12 .75 7.62.75 3.84 3.27 2.02 6.93l3.66 2.84C6.55 7.1 8.99 5.04 12 5.04z" />
      <path fill="#4285F4" d="M23.25 12.26c0-.87-.08-1.5-.25-2.16H12v4.09h6.44c-.13 1.08-.83 2.7-2.39 3.79l3.57 2.77c2.14-1.97 3.63-4.88 3.63-8.49z" />
      <path fill="#FBBC05" d="M5.69 14.23a6.9 6.9 0 0 1 0-4.46L2.02 6.93a11.26 11.26 0 0 0 0 10.14l3.67-2.84z" />
      <path fill="#34A853" d="M12 23.25c3.04 0 5.6-1 7.46-2.73l-3.57-2.77c-.95.66-2.24 1.12-3.89 1.12-3.01 0-5.45-2.05-6.32-4.64l-3.66 2.84c1.82 3.66 5.6 6.18 9.98 6.18z" />
    </svg>
    Entrar com Google
  </button>
);

const LoginForm = ({ onSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(formatApiErrorDetail(data.detail, "Erro ao entrar"));
      }
      if (data.role !== "admin") {
        throw new Error("Esta conta não tem acesso ao painel.");
      }
      onSuccess(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="gradient-ring w-full max-w-md rounded-2xl p-8 shadow-[0_24px_80px_rgba(0,0,0,0.6)]">
      <div className="flex items-center justify-between">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#E50914] shadow-[0_0_28px_rgba(229,9,20,0.45)]">
          <Lock className="h-6 w-6 text-white" />
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-[#22222A] bg-[#050505] px-3 py-1 font-mono-code text-[10px] uppercase tracking-widest text-zinc-500">
          <ShieldCheck className="h-3 w-3 text-emerald-400" />
          Acesso restrito
        </span>
      </div>
      <h1 className="mt-6 font-display text-2xl font-bold text-zinc-100">Painel Admin</h1>
      <p className="mt-2 text-sm text-zinc-500">
        Área restrita do LUIZ.DEV. Entre com sua conta para ver as conversas do chat e as estatísticas.
      </p>

      <form onSubmit={submit} className="mt-8 space-y-4">
        <div className="relative">
          <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-600" />
          <input
            data-testid="admin-login-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Seu e-mail"
            className="h-12 w-full rounded-xl border border-[#22222A] bg-[#050505] pl-11 pr-4 text-sm text-zinc-100 outline-none transition-colors placeholder:text-zinc-600 focus:border-[#E50914]/60"
          />
        </div>
        <div className="relative">
          <Lock className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-600" />
          <input
            data-testid="admin-login-password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Sua senha"
            className="h-12 w-full rounded-xl border border-[#22222A] bg-[#050505] pl-11 pr-4 text-sm text-zinc-100 outline-none transition-colors placeholder:text-zinc-600 focus:border-[#E50914]/60"
          />
        </div>
        {error && (
          <div data-testid="admin-login-error" className="rounded-xl border border-[#E50914]/40 bg-[#E50914]/10 px-4 py-3 text-sm text-[#FF2A3B]">
            {error}
          </div>
        )}
        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={loading}
          className="btn-red-glow flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#E50914] font-mono-code text-sm font-bold uppercase tracking-widest text-white disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Entrar"}
        </button>
      </form>

      <div className="my-6 flex items-center gap-4">
        <span className="h-px flex-1 bg-[#22222A]" />
        <span className="font-mono-code text-[10px] uppercase tracking-widest text-zinc-600">ou</span>
        <span className="h-px flex-1 bg-[#22222A]" />
      </div>

      <GoogleButton />

      <a
        data-testid="admin-back-to-site"
        href="/"
        className="mt-6 flex items-center justify-center gap-2 text-xs text-zinc-500 transition-colors hover:text-[#E50914]"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Voltar ao site
      </a>
    </div>
  );
};

const Dashboard = ({ user, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [s, c] = await Promise.all([
          fetch(`${API}/admin/stats`, { credentials: "include" }).then((r) => r.json()),
          fetch(`${API}/admin/conversations`, { credentials: "include" }).then((r) => r.json()),
        ]);
        setStats(s);
        setConversations(c.conversations || []);
      } catch {}
    };
    load();
  }, []);

  const openConversation = async (sessionId) => {
    setSelected(sessionId);
    setDetail(null);
    try {
      const d = await fetch(`${API}/admin/conversations/${sessionId}`, { credentials: "include" }).then((r) =>
        r.json()
      );
      setDetail(d.messages || []);
    } catch {
      setDetail([]);
    }
  };

  const statCards = stats
    ? [
        { label: "Visitas no site", value: stats.visits, icon: Eye, testid: "admin-stat-visits" },
        { label: "Conversas no chat", value: stats.conversations, icon: MessagesSquare, testid: "admin-stat-conversations" },
        { label: "Mensagens totais", value: stats.messages, icon: MessageSquare, testid: "admin-stat-messages" },
        { label: "Mensagens hoje", value: stats.messages_today, icon: Bot, testid: "admin-stat-today" },
      ]
    : [];

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-10 sm:px-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 font-mono-code text-xs uppercase tracking-[0.3em] text-[#E50914]">
            <ShieldCheck className="h-4 w-4" />
            Área protegida
          </div>
          <h1 className="mt-2 font-display text-3xl font-extrabold text-zinc-100">
            Painel <span className="text-[#E50914]">Admin</span>
          </h1>
          <p className="mt-1 text-sm text-zinc-500">Logado como {user.email}</p>
        </div>
        <div className="flex gap-3">
          <a
            data-testid="admin-view-site-btn"
            href="/"
            className="rounded-full border border-[#2A2A33] px-5 py-2.5 font-mono-code text-xs uppercase tracking-widest text-zinc-300 transition-colors hover:border-[#E50914]/60 hover:text-white"
          >
            Ver site
          </a>
          <button
            data-testid="admin-logout-btn"
            onClick={onLogout}
            className="flex items-center gap-2 rounded-full bg-[#E50914] px-5 py-2.5 font-mono-code text-xs font-bold uppercase tracking-widest text-white transition-shadow hover:shadow-[0_0_24px_rgba(229,9,20,0.4)]"
          >
            <LogOut className="h-4 w-4" />
            Sair
          </button>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {statCards.map((card, i) => (
          <motion.div
            key={card.label}
            data-testid={card.testid}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
            className="card-glow gradient-ring relative overflow-hidden rounded-2xl p-6"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E50914]/10">
              <card.icon className="h-5 w-5 text-[#E50914]" />
            </div>
            <div className="mt-4 font-display text-3xl font-extrabold text-zinc-100">{card.value}</div>
            <div className="mt-1 font-mono-code text-[10px] uppercase tracking-[0.2em] text-zinc-500">{card.label}</div>
          </motion.div>
        ))}
      </div>

      <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-5">
        <div className="rounded-2xl border border-[#22222A] bg-[#101014] lg:col-span-2">
          <div className="border-b border-[#1A1A20] px-6 py-4">
            <h2 className="font-display text-lg font-bold text-zinc-100">Conversas do chat</h2>
            <p className="text-xs text-zinc-500">Todo visitante que falou com a IA</p>
          </div>
          <div className="max-h-[480px] overflow-y-auto p-3">
            {conversations.length === 0 && (
              <p className="p-6 text-center text-sm text-zinc-600">Nenhuma conversa ainda.</p>
            )}
            {conversations.map((c) => (
              <button
                key={c.session_id}
                data-testid={`admin-conversation-item-${c.session_id.slice(0, 8)}`}
                onClick={() => openConversation(c.session_id)}
                className={`w-full rounded-xl p-4 text-left transition-colors ${
                  selected === c.session_id ? "bg-[#E50914]/15" : "hover:bg-[#0A0A0C]"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono-code text-xs text-zinc-400">#{c.session_id.slice(0, 8)}</span>
                  <span className="text-[10px] text-zinc-600">{formatDate(c.last_at)}</span>
                </div>
                <p className="mt-1.5 line-clamp-2 text-sm text-zinc-400">"{c.preview}"</p>
                <span className="mt-2 inline-block rounded-full bg-[#E50914]/10 px-2.5 py-0.5 font-mono-code text-[10px] text-[#FF2A3B]">
                  {c.messages} mensagens
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-[#22222A] bg-[#101014] lg:col-span-3" data-testid="admin-conversation-detail">
          <div className="border-b border-[#1A1A20] px-6 py-4">
            <h2 className="font-display text-lg font-bold text-zinc-100">
              {selected ? `Conversa #${selected.slice(0, 8)}` : "Selecione uma conversa"}
            </h2>
          </div>
          <div className="max-h-[480px] space-y-3 overflow-y-auto p-6">
            {!selected && <p className="py-10 text-center text-sm text-zinc-600">Clique em uma conversa ao lado para ler as mensagens.</p>}
            {selected && !detail && (
              <div className="flex justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-[#E50914]" />
              </div>
            )}
            {detail?.map((m, i) => (
              <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role === "assistant" && (
                  <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#E50914]">
                    <Bot className="h-4 w-4 text-white" />
                  </div>
                )}
                <div
                  data-testid={`admin-message-${m.role}-${i}`}
                  className={`max-w-[80%] whitespace-pre-wrap rounded-2xl p-3.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "rounded-tr-sm bg-[#E50914] text-white"
                      : "rounded-tl-sm border border-[#22222A] bg-[#0A0A0C] text-zinc-300"
                  }`}
                >
                  {m.content}
                  <div className={`mt-1.5 text-[10px] ${m.role === "user" ? "text-white/60" : "text-zinc-600"}`}>
                    {formatDate(m.timestamp)}
                  </div>
                </div>
                {m.role === "user" && (
                  <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[#2A2A33] bg-[#0A0A0C]">
                    <User className="h-4 w-4 text-zinc-400" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <AdminQuotes />

      <AdminProjects />

      <AdminPayments />
    </div>
  );
};

export default function AdminPage() {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const processed = useRef(false);

  useEffect(() => {
    if (location.hash?.includes("session_id=")) {
      if (processed.current) return;
      processed.current = true;
      const sessionId = new URLSearchParams(location.hash.slice(1)).get("session_id");
      fetch(`${API}/auth/google/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionId }),
      })
        .then((r) => {
          if (!r.ok) throw new Error("Falha no login com Google");
          return r.json();
        })
        .then((u) => {
          window.history.replaceState(null, "", "/admin");
          if (u.role !== "admin") {
            setUser(false);
          } else {
            setUser(u);
          }
        })
        .catch(() => setUser(false));
      return;
    }
    fetch(`${API}/auth/me`, { credentials: "include" })
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then((u) => setUser(u.role === "admin" ? u : false))
      .catch(() => setUser(false));
  }, [location.hash]);

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" }).catch(() => {});
    setUser(false);
  };

  return (
    <div className="flex min-h-screen flex-col bg-[#050505] text-zinc-100">
      <div className="noise-overlay" aria-hidden="true" />
      {user === null && <PageLoader testid="admin-loading" label="Validando credenciais" />}
      {user === false && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-1 items-center justify-center px-4 py-16"
        >
          <LoginForm onSuccess={setUser} />
        </motion.div>
      )}
      {user && user.role === "admin" && <Dashboard user={user} onLogout={logout} />}
    </div>
  );
}
